import type {
  BroadcastContent,
  ConnectingDelayJudgementMode,
  ConnectingDirectionEntry,
  ConnectingLineEntry,
  DelayBroadcastPattern,
} from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import type { RouteMaster } from '../../../data/routes';
import type { RouteStop } from '../../../data/routeStops';

import {
  PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST_LINE,
  PHRASE_TEMPLATE_CONNECTING_STATION_LINE,
} from '../../../data/phraseTemplates';
import { usePhraseTemplateDefaults } from '../../../contexts/PhraseTemplateContext';
import {
  applyConnectingStationJudgementMode,
  CONNECTING_STATION_LINE_DEFAULT_SSML,
  CONNECTING_STATION_DEPARTURE_FORECAST_LINE_DEFAULT_SSML,
  DEFAULT_CONNECTING_STATION_DELAY_PATTERNS,
  isDepartureForecastMode,
  requiresConnectingStationDirections,
  resolveEffectiveSsml,
  resolveLocalizedDefaults,
  resolveConnectingSearchRouteIds,
} from '../../../utils/broadcastContents';
import type { RevealTarget, ValidationIssue } from '../../../utils/validationIssues';
import { hasIssueForTestId } from '../../../utils/validationIssues';

// 数値入力は共通の NumericInput を使う。
// 素の <input type="number" value={number}> だと Number('') === 0 のため
// 既定値 0 を消せない不具合が起きる (NumericInput 側のコメント参照)。
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';
import { CONNECTING_DELAY_MODE_META } from '../constants';
import { ConnectingRouteIdsSelect } from '../parts/ConnectingRouteIdsSelect';
import { IStationsLinesSelect } from '../parts/IStationsLinesSelect';
import { DepartureForecastIStationsWarning } from '../parts/DepartureForecastIStationsWarning';

import type { PreviewSsmlHandler } from '../types';
import { ConnectingDirectionsSection } from './ConnectingDirectionsSection';
import { LocalizedRouteNicknameFields } from '../parts/LocalizedRouteNicknameFields';
import { RouteNameGuide } from '../parts/RouteNameGuide';
import { LocalizedTextFields } from '../parts/LocalizedTextFields';
import {
  LOCALIZED_STATION_DEPARTURE_FORECAST_LINE,
  LOCALIZED_STATION_LINE,
} from '../../../utils/localizedSsml';
import { syncRouteNickname } from '../../../utils/routeNicknameDefaults';
import { ConnectingStationLineDelayPatternsTable } from './ConnectingStationLineDelayPatternsTable';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ぶんのブロック。
 * ConnectingLineDelayByStationFields.lines[i] を編集する。
 *
 * 内部で「route_id 選択 → 遅延検索対象の線区 (route_ids) → 判定モード →
 * 乗換時間 (transfer_aware) → 方面 (1〜2件) → 接続線ブロック SSML →
 * 遅延放送パターン (方面部分文SSML)」を並べる。
 * 判定モードによる項目表示切替のルールは part1 (`ConnectingLineDelayFields`) と同じ。
 */
export function ConnectingLineBlock({
  index,
  line,
  routes,
  routeStops,
  istationsLines = [],
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  canRemove,
  isFirst,
  isLast,
  issues = [],
  revealTarget = null,
  onChange,
  onAdd,
  onDuplicate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  line: ConnectingLineEntry;
  /**
   * この接続線に紐づく入力エラー (親で `lineIndex` 絞り込み済み)。
   * route_id / 判定モード / 乗換時間 / ブロック SSML 欄の赤枠と、
   * 方面タブの ⚠ マークに使う。
   */
  issues?: ValidationIssue[];
  /** 方面タブを自動で開くための指示 (`ConnectingDirectionsSection` へ中継する)。 */
  revealTarget?: RevealTarget | null;

  /**
   * プレビュー対象の放送コンテンツ (Issue #439)。
   * 方面部分文 SSML の行ごとのプレビューで、サーバへコンテンツ全体を渡すために使う
   * (part2 は「冒頭文 + ブロック文 + 方面部分文」を三層で組み立てるため、
   *  方面部分文だけを単体合成すると本番と違う文面になってしまう)。
   */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 「運行情報の参照元（アイステイションズ路線）」の選択肢マスタ。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   */
  istationsLines?: IStationsLine[];
  canRemove: boolean;

  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ConnectingLineEntry) => void;
  onAdd: () => void;
  /**
   * この接続線ブロックを **複製** して直後に挿入する
   * (受入条件「設定を他の接続線ブロックへコピーできること」)。
   *
   * 実際のディープコピーは親 (`ConnectingLineDelayByStationFields`) 側で
   * `cloneConnectingStationLine()` を用いて行う。方面や遅延パターンの配列を
   * 参照共有したまま複製すると、複製元を編集した際に複製先も一緒に変わって
   * しまうため、コピー規則は純関数 1 箇所に集約している。
   */
  onDuplicate: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const routeId = line.route_id ?? '';
  const languages = content?.languages ?? ['ja'];
  const judgementMode: ConnectingDelayJudgementMode = line.delay_judgement_mode ?? 'whole_line';
  const transferTimeSeconds = line.transfer_time_seconds ?? 180;
  /*
   * Issue #552: 接続線ブロック SSML の入力値は **未設定なら空欄のまま** にする。
   *
   * 以前はコード内既定文面 (`CONNECTING_STATION_LINE_DEFAULT_SSML`) を値として
   * 流し込んでいたため、placeholder (薄字) が一度も表示されず、
   * かつ他項目を触った拍子に既定文面が実値として保存されて
   * 以後マスタ編集が反映されなくなっていた (Issue #527 の規約違反)。
   */
  const isDepartureForecast = isDepartureForecastMode(judgementMode);
  const lineTemplate = line.ssml_line_template ?? '';
  /*
   * Issue #552: 定型文設定 (`connecting_station_line`) の既定文面。
   * 「薄字 = 実際に放送される文面」を成立させるため、
   * マスタ (日本語) → コード内既定 の順に解決する。
   */
  const resolveDefault = usePhraseTemplateDefaults();
  // SBI-5: 発車見込み案内は専用のマスタ ID / 既定文 (語尾「発車の見込みです」を担う)。
  const lineTemplateId = isDepartureForecast
    ? PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST_LINE
    : PHRASE_TEMPLATE_CONNECTING_STATION_LINE;
  const effectiveLineTemplate = resolveEffectiveSsml(
    line.ssml_line_template,
    lineTemplateId,
    isDepartureForecast
      ? CONNECTING_STATION_DEPARTURE_FORECAST_LINE_DEFAULT_SSML
      : CONNECTING_STATION_LINE_DEFAULT_SSML,
    resolveDefault,
  );
  const directions: ConnectingDirectionEntry[] = Array.isArray(line.directions)
    ? line.directions
    : [];
  const patterns: DelayBroadcastPattern[] = Array.isArray(line.delay_patterns)
    ? line.delay_patterns
    : DEFAULT_CONNECTING_STATION_DELAY_PATTERNS.map((p) => ({ ...p }));

  // 判定モードによる項目表示切替 (part1 と同じ考え方)。
  //   whole_line       : **方面セクションを丸ごと非表示**。接続線全体の最大遅延だけで判定し、
  //                      放送文でも方面名を読み上げないため、方面の入力は一切不要。
  //                      (方面部分文 SSML も {direction_name} を含まない文面が既定になる)
  //   section_scheduled: 方面ごとに区間 (start/end_stop_id) 必須。
  //   transfer_aware   : 乗換時間 (秒) 必須。区間は不要 (方面名は案内文面用に必要)。
  const isTransferAwareMode = judgementMode === 'transfer_aware';
  // SBI-5: 発車見込み案内も transfer_aware と同じく乗換時間 (秒) と方面が必要
  // (接続便の特定が共通のため)。
  const showTransferTime = isTransferAwareMode || isDepartureForecast;
  // 方面セクションを表示するか。判定規則は utils に集約 (バリデーションと同じ関数を使う)。
  const showDirections = requiresConnectingStationDirections(judgementMode);

  // 表示ラベル用: 接続線の一般的な線名 (routes-master.json の route_name)。
  const currentRoute = routes.find((r) => r.route_id === routeId);
  const routeDisplayName = currentRoute?.route_name || routeId;

  /** ブロック全体を patch で更新 */
  const emit = (patch: Partial<ConnectingLineEntry>): void => {
    onChange({ ...line, ...patch });
  };

  return (
    <div className="connecting-line-block" data-testid={`connecting-line-block-${index}`}>
      {/* ブロックヘッダ: ラベル + 操作ボタン (上/下/追加/複製/削除) */}
      <div
        className="connecting-line-block-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <strong>
          接続線 #{index + 1}
          {routeId ? `（${routeDisplayName}）` : ''}
        </strong>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            aria-label={`接続線#${index + 1} を上へ`}
            data-testid={`connecting-line-move-up-${index}`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`接続線#${index + 1} を下へ`}
            data-testid={`connecting-line-move-down-${index}`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            ↓
          </button>
          <button
            type="button"
            aria-label={`接続線#${index + 1} の後に追加`}
            data-testid={`connecting-line-add-${index}`}
            onClick={onAdd}
          >
            ＋ 追加
          </button>
          {/*
            接続線ブロック単位の複製。判定モード / 遅延検索対象の線区 / 方面 /
            遅延パターン / 各 SSML テンプレートを一式引き継いだブロックを直後に作る。
            方面 (最大 2 件) や遅延パターン (4 行) と多言語 SSML を 1 件ずつ
            入力し直す手間が大きいため、同一放送地点内のコピー手段を用意する。
            放送地点をまたぐコピーは、放送地点一覧の
            「複製」(CopyDialog) が担当する (あちらは地点単位)。
          */}
          <button
            type="button"
            aria-label={`接続線#${index + 1} を複製`}
            data-testid={`connecting-line-duplicate-${index}`}
            title="この接続線ブロックの設定を丸ごとコピーして、すぐ下に追加します"
            onClick={onDuplicate}
          >
            📋 複製
          </button>
          {canRemove ? (
            <button
              type="button"
              className="danger"
              aria-label={`接続線#${index + 1} を削除`}
              data-testid={`connecting-line-remove-${index}`}
              onClick={onRemove}
            >
              × 削除
            </button>
          ) : null}
        </div>
      </div>

      {/*
        「接続線 route_id」と「接続線 線区愛称（任意）」は二段組 (横並び) にする。
        どちらも 1 行で収まる短い項目であり、縦積みにすると接続線ブロックが
        不要に縦長になるため `.field-pair-2col` で囲む
        (狭幅では CSS side で 1 列へフォールバックする)。
      */}
      <div className="field-pair-2col" data-testid={`connecting-line-route-pair-${index}`}>
        {/*
            左セル: 遅延を検索する線区 (先頭 = route_id / 必須、2 件目以降 = route_ids / 任意)。

            画面上は先頭とそれ以外を区別せず、1 つの一覧 + 1 つのプルダウンで編集する。
            保存形式だけ、一覧の先頭が route_id、2 件目以降が route_ids になる。

            検索対象 = route_id (常に含まれる) + route_ids。
            複数の線区をまとめて検索し、1 つの案内文を生成する
            (乗り換え列車の遅延・発車見込みは発車が最も早い接続列車、接続線全体などは最大遅延)
            (例: 大宮の高崎線下りは「東北下り」と「東北貨物下り」の遅れの影響を受ける)。
            route_ids 未設定なら route_id のみを検索する (後方互換)。
          */}
        <ConnectingRouteIdsSelect
          testIdPrefix={`connecting-line-route-ids-${index}`}
          ariaLabelPrefix={`接続線#${index + 1} `}
          routes={routes}
          routeId={routeId}
          routeIds={Array.isArray(line.route_ids) ? line.route_ids : []}
          // 線区が 1 つも選ばれていないまま保存しようとしたときのエラーは、このプルダウンを指す。
          selectTestId={`connecting-line-route-id-${index}`}
          selectInvalid={hasIssueForTestId(issues, `connecting-line-route-id-${index}`)}
          // 重複 / 上限超過は保存時のバリデーションでも検出する。
          // 該当したときはこの選択欄を赤枠にして場所を示す。
          invalid={hasIssueForTestId(issues, `connecting-line-route-ids-${index}`)}
          onChange={(next) =>
            emit({
              route_id: next.routeId,
              // 空配列は「未設定」として落とし、DB / JSON にゴミを残さない
              // (サーバ側も空配列はキーごと保存しない = 既存 item と bit-perfect 互換)。
              route_ids: next.routeIds.length > 0 ? next.routeIds : undefined,
              // 先頭の線区 (route_id) が変わったら方面の区間駅はリセット (別路線の stop_id が残らないように)。
              // 線区愛称も、自動入力のままなら新しい先頭のマスタ名称へ追従させる
              // (手入力した愛称は消さない。規則は `syncRouteNickname` を参照)。
              ...(next.routeId !== routeId
                ? {
                    directions: directions.map((d) => ({
                      ...d,
                      start_stop_id: undefined,
                      end_stop_id: undefined,
                    })),
                    ...syncRouteNickname(routeId, next.routeId, line, routes, languages),
                  }
                : {}),
            })
          }
        />

        {/*
          右セル: 接続線ブロック単位の線区愛称 (必須)。放送 SSML の
          {route_name} / {route} プレースホルダに最優先で使用される。
          線区を選ぶと線区一覧の名称が自動入力される (手入力で上書き可)。
          愛称を必ず入れることで、実際に放送される線名が画面の値と一致する。
          空欄の保存は画面のバリデーションで止める (API は既存データ互換のため必須にしない)。
        */}
        <label>
          <span className="required">接続線 線区愛称</span>
          <input
            type="text"
            aria-label={`接続線#${index + 1} 線区愛称`}
            data-testid={`connecting-line-route-nickname-${index}`}
            className={
              hasIssueForTestId(issues, `connecting-line-route-nickname-${index}`)
                ? 'is-invalid'
                : ''
            }
            value={typeof line.route_nickname === 'string' ? line.route_nickname : ''}
            onChange={(e) => emit({ route_nickname: e.target.value })}
            placeholder="例: 湘南新宿ライン"
          />
          <span className="hint" style={{ fontSize: 12, color: '#555' }}>
            放送の <code>{'{route_name}'}</code> / <code>{'{route}'}</code>{' '}
            に使われる線名です。線区を選ぶと自動で入力されます（変更もできます）。
          </span>
          {/*
              Issue #579 (SBI-09): この接続線ブロックの線区愛称の **各言語名称**。
              接続線ごとに独立して持てるので testIdPrefix に index を含める。
            */}
          <LocalizedRouteNicknameFields
            label={`接続線#${index + 1} 線区愛称`}
            required
            languages={languages}
            value={line.route_nickname_i18n}
            japaneseNickname={typeof line.route_nickname === 'string' ? line.route_nickname : ''}
            testIdPrefix={`connecting-line-route-nickname-${index}`}
            onChange={(value) => emit({ route_nickname_i18n: value })}
          />
          {/*
              愛称が未設定のとき、放送で実際に案内される線名を言語ごとに示す。
              入力欄のすぐ下に置き、入力の途中で結果を確認できるようにする。
            */}
          <RouteNameGuide
            testId={`connecting-line-route-name-guide-${index}`}
            routeId={routeId}
            nickname={line.route_nickname}
            nicknameI18n={line.route_nickname_i18n}
            languages={languages}
          />
        </label>
      </div>

      {/*
        運行情報の参照元 (アイステイションズ路線) の複数選択。

        part2 は接続線ごと (lines[].istations_lines) に保持する。
        上の「接続線 route_id」(ATOS) とは連動させず、一致しない路線も自由に選べる。
      */}
      <IStationsLinesSelect
        testIdPrefix={`connecting-line-istations-lines-${index}`}
        ariaLabelPrefix={`接続線#${index + 1} `}
        options={istationsLines}
        value={Array.isArray(line.istations_lines) ? line.istations_lines : []}
        onChange={(nextLines) =>
          // 空配列は「未設定」として落とし、DB / JSON にゴミを残さない
          // (サーバ側も空配列はキーごと保存しない)。
          emit({ istations_lines: nextLines.length > 0 ? nextLines : undefined })
        }
      />

      {/*
        ここから下 (判定モード / 乗換時間 / 方面 / ブロック SSML / 遅延放送パターン) は
        すべて **遅延判定と遅延放送文の組み立て** に関する設定。
      */}
      {/* 判定モード */}

      <label>
        <span className="required">判定モード</span>

        <select
          aria-label={`接続線#${index + 1} 判定モード`}
          data-testid={`connecting-line-judgement-mode-${index}`}
          value={judgementMode}
          onChange={(e) => {
            const nextMode = e.target.value as ConnectingDelayJudgementMode;
            // モード切替時の値の整理 (使わない項目のクリア / 方面の畳み込み /
            // 方面部分文デフォルト文の差し替え) は utils の純関数に集約する。
            // 詳細は `applyConnectingStationJudgementMode` の JSDoc を参照。
            onChange(
              applyConnectingStationJudgementMode(
                { ...line, directions, delay_patterns: patterns },
                nextMode,
              ),
            );
          }}
        >
          {CONNECTING_DELAY_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>
      <p className="hint" style={{ margin: '4px 0' }}>
        {judgementMode === 'whole_line'
          ? '接続線全体の最大遅延で判定します。方面ごとの区間指定・方面名の入力はいずれも不要です (放送文でも方面は読み上げません)。'
          : judgementMode === 'section_scheduled'
            ? '指定区間に定刻在線している便の遅延で判定します。方面ごとに区間 (開始駅・終了駅) を指定してください。'
            : judgementMode === 'departure_forecast'
              ? 'お客さまがまさに乗り換えようとしている接続列車について、遅延分数ではなく「発車の見込み時刻」を案内します。乗換時間 (秒) と方面が必須です。'
              : 'お客さまがまさに乗り換えようとしている接続列車の遅延で判定します。乗換時間 (秒) が必須です。'}
      </p>

      {/* 発車見込み案内で「運行情報と連携」が未設定のときの警告 (入力は必須にしない) */}
      {isDepartureForecast ? (
        <DepartureForecastIStationsWarning
          istationsLines={line.istations_lines}
          testId={`connecting-line-departure-forecast-istations-warning-${index}`}
        />
      ) : null}

      {/* 乗換時間 (transfer_aware / departure_forecast) */}
      {showTransferTime ? (
        <label>
          <span className="required">乗換時間 (秒)</span>
          <NumericInput
            min={0}
            step={1}
            aria-label={`接続線#${index + 1} 乗換時間 (秒)`}
            data-testid={`connecting-line-transfer-time-${index}`}
            className={
              hasIssueForTestId(issues, `connecting-line-transfer-time-${index}`)
                ? 'is-invalid'
                : undefined
            }
            value={transferTimeSeconds}
            normalizeValue={clampNonNegativeInt}
            onChange={(nextTransferTime) => emit({ transfer_time_seconds: nextTransferTime })}
          />
        </label>
      ) : null}

      {/*
            方面ブロック (1〜2件)。

            whole_line (接続線全体の遅延) では方面の入力が一切不要なので **表示しない**。
            「画面に出ているのに放送へ反映されない」誤解と、必須マークの付いた項目が
            残ることによる保存不能を同時に避ける (バリデーション側も免除している)。
            なお内部的には方面 1 件を保持する (放送文・遅延判定の繰り返し単位のため)。
          */}
      {showDirections ? (
        <ConnectingDirectionsSection
          lineIndex={index}
          routeId={routeId}
          // 「遅延を判定する向き」表 (route_directions) の行に使う。
          // route_id + route_ids の検索対象 (放送側と同じ規則)。route_id も行に出す。
          routeIds={resolveConnectingSearchRouteIds(line)}
          judgementMode={judgementMode}
          routes={routes}
          routeStops={routeStops}
          directions={directions}
          // 方面タブの ⚠ マーク / 方面名・区間駅の赤枠に使う
          // (この接続線ぶんの issues をそのまま渡し、下位で directionIndex を見る)。
          issues={issues}
          // 方面タブを自動で開くための指示。
          revealTarget={revealTarget}
          // 方面名の言語別入力欄 (direction_name_i18n) の表示に使う (モンキーテスト B-1)。
          languages={languages}
          onChangeDirections={(nextDirections) => emit({ directions: nextDirections })}
        />
      ) : null}

      {/* 接続線ブロック SSML テンプレート */}
      {languages.includes('ja') ? (
        <label>
          {/*
                Issue #527: 空欄 = 定型文設定 (`connecting_station_line`) へ委譲する
                規約へ変更したため **必須ではない**。
              */}
          <span>接続線ブロック SSML テンプレート</span>
          <input
            type="text"
            aria-label={`接続線#${index + 1} ブロック SSML テンプレート`}
            data-testid={`connecting-line-block-template-${index}`}
            className={
              hasIssueForTestId(issues, `connecting-line-block-template-${index}`)
                ? 'is-invalid'
                : ''
            }
            value={lineTemplate}
            /*
                  Issue #552: 薄字 (placeholder) は「未入力時に実際に放送される文面」。
                  定型文設定 (`connecting_station_line`) に登録があればその文面、
                  無ければコード内既定へフォールバックする
                  (放送側 `_render_connecting_station_line()` と同じ解決順)。
                */
            placeholder={effectiveLineTemplate}
            onChange={(e) => emit({ ssml_line_template: e.target.value })}
          />
        </label>
      ) : null}
      <LocalizedTextFields
        label={`接続線#${index + 1} ブロック SSML テンプレート`}
        languages={languages}
        value={line.ssml_line_template_i18n}
        // Issue #552: 各言語の既定文面も定型文設定 (`connecting_station_line`) を
        // 反映する (マスタ未登録の言語はコード内既定のまま)。
        defaults={resolveLocalizedDefaults(
          isDepartureForecast ? LOCALIZED_STATION_DEPARTURE_FORECAST_LINE : LOCALIZED_STATION_LINE,
          lineTemplateId,
          resolveDefault,
        )}
        // Issue #556: 既定文面は **薄字 (placeholder) 専用**。値として入れると
        // 定型文マスタの多言語編集が反映されなくなる (空欄 = マスタ委譲を守る)。
        defaultsAreValues={false}
        // Issue #492 (SBI-11): 翻訳元は同じ接続線ブロックの日本語テンプレート。
        //
        // Issue #552: 未入力 (= マスタ委譲) のときは実効文面を翻訳元にする
        // (空文字だと「日本語から翻訳」ボタンが押せない)。
        sourceText={effectiveLineTemplate}
        translateTestId={`connecting-line-block-${index}`}
        onChange={(value) => emit({ ssml_line_template_i18n: value })}
      />
      {/* 遅延放送パターン (方面部分文の SSML) */}
      <ConnectingStationLineDelayPatternsTable
        lineIndex={index}
        patterns={patterns}
        // デフォルト文言 / 凡例をモード別に出し分けるため判定モードを渡す。
        judgementMode={judgementMode}
        // Issue #439: 行ごとの音声プレビューに必要な情報を渡す。
        content={content}
        contentIndex={contentIndex}
        parentVoiceId={parentVoiceId}
        playbackRate={playbackRate}
        onPreviewSsml={onPreviewSsml}
        // どの行 (パターン) にエラーがあるかを行単位で赤く示すために渡す。
        issues={issues}
        onChange={(nextPatterns) => emit({ delay_patterns: nextPatterns })}
      />
    </div>
  );
}
